import type { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '../../types/api';
import axios from 'axios';
import { ENV } from '../../config/env';

const API_BASE_URL = `${ENV.VITE_API_URL}${ENV.VITE_API_BASE_PATH || '/api/v1'}`;

let isRefreshing = false;
let refreshSubscribers: Array<(error?: any) => void> = [];

function onRefreshed(error?: any) {
  refreshSubscribers.forEach((cb) => cb(error));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (error?: any) => void) {
  refreshSubscribers.push(cb);
}

function redirectToLogin() {
  const isLoginPage = window.location.pathname === '/login';
  if (!isLoginPage) {
    window.location.href = '/login';
  }
}

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

export function setupResponseInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse<any>>) => {
      return response;
    },
    async (error: AxiosError<ApiResponse<any>>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // If 401 and not already retrying, try refresh token
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          // Mark as retried so a still-401 response after the refresh can never
          // re-enter this branch and start another refresh cycle.
          originalRequest._retry = true;
          // Wait for refresh to finish, then retry or fail
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((refreshError?: any) => {
              if (refreshError) {
                reject(refreshError);
              } else {
                resolve(instance(originalRequest));
              }
            });
          });
        }

        isRefreshing = true;
        originalRequest._retry = true;

        try {
          await refreshClient.post('/auth/refresh');
          onRefreshed();
          // Retry the original request — cookies are sent automatically
          return instance(originalRequest);
        } catch (refreshError: any) {
          onRefreshed(refreshError);
          redirectToLogin();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      const responseData = (await decodeErrorPayload(error.response?.data)) as any;
      const reasons = Array.isArray(responseData?.error)
        ? responseData.error.map((e: any) => e?.reason).filter(Boolean)
        : [];
      // Prefer a specific server message. The API collapses validation failures
      // to "Validation failed", so surface the individual field reasons instead.
      const specificMessage =
        responseData?.message && responseData.message !== 'Validation failed'
          ? responseData.message
          : undefined;
      const message =
        specificMessage ||
        (reasons.length ? reasons.join(', ') : undefined) ||
        error.message ||
        'An error occurred';

      // Rewrite the body's message so components (and the global mutation
      // toaster) that read `error.response.data.message` also show the
      // friendly, specific message rather than "Validation failed".
      const normalizedData =
        responseData && typeof responseData === 'object'
          ? { ...responseData, message }
          : responseData;

      const apiError = {
        status: error.response?.status,
        code: responseData?.error?.[0]?.code || responseData?.statusCode || 'UNKNOWN_ERROR',
        message,
        details: responseData?.error,
        // Preserve the original response (with the decoded body) so callers that
        // still read `error.response.data.message` receive the friendly message.
        response: error.response
          ? { ...error.response, data: normalizedData }
          : undefined,
      };

      return Promise.reject(apiError);
    },
  );
}

/**
 * Binary downloads (e.g. report exports requested with `responseType: 'blob'`)
 * still receive a JSON error body when the request fails. Decode it so callers
 * can surface the real validation message instead of a generic HTTP status.
 */
async function decodeErrorPayload(data: unknown): Promise<unknown> {
  if (!data || typeof data !== 'object') return data;

  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      const text = await data.text();
      return text ? JSON.parse(text) : data;
    }
    if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
      const text = new TextDecoder().decode(data);
      return text ? JSON.parse(text) : data;
    }
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
  } catch {
    // Not JSON (or unreadable) — keep the original payload.
  }

  return data;
}
